package com.rosario.juridico.ui.screens

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.automirrored.filled.Chat
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.navigation.NavController
import com.rosario.juridico.domain.AppUsageManager
import com.rosario.juridico.domain.model.CartaDocumentoItem
import com.rosario.juridico.domain.model.MediacionGasto
import com.rosario.juridico.ui.components.JuridicoDatePickerDialogKMP
import com.rosario.juridico.ui.screens.TrialActionLock
import com.rosario.juridico.platform.getDocumentExporter
import kotlinx.coroutines.launch

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun PantallaPlanillaCierreMediacion(navController: NavController) {
    val viewModel = MediacionGastoViewModel.instance
    val gasto by viewModel.mediacionGasto
    val cartas = viewModel.cartasDocumento
    
    LaunchedEffect(Unit) {
        viewModel.loadUserData()
    }
    
    var showPreview by remember { mutableStateOf(false) }

    Scaffold(
        topBar = {
            Column {
                CenterAlignedTopAppBar(
                    title = { Text("Planilla de Cierre", fontWeight = FontWeight.Bold) },
                    navigationIcon = {
                        IconButton(onClick = { navController.popBackStack() }) {
                            Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = "Atrás")
                        }
                    },
                    actions = {
                        IconButton(onClick = { showPreview = !showPreview }) {
                            Icon(if (showPreview) Icons.Default.Edit else Icons.Default.Visibility, contentDescription = "Cambiar vista")
                        }
                    }
                )
            }
        }
    ) { padding ->
        if (showPreview) {
            PreviewPlanilla(navController, gasto.copy(cartasDocumento = cartas.toList()), padding)
        } else {
            FormularioPlanilla(
                gasto = gasto,
                cartas = cartas,
                onGastoChange = { viewModel.mediacionGasto.value = it },
                onAddCarta = { viewModel.addCartaDocumento() },
                onRemoveCarta = { viewModel.removeCartaDocumento(it) },
                onUpdateCarta = { id, n, c, ci, m -> viewModel.updateCartaDocumento(id, n, c, ci, m) },
                onSaveMediador = { viewModel.saveMediadorData() },
                onSaveOficina = { viewModel.saveOficinaData() },
                onSavePago = { viewModel.savePagoData() },
                padding = padding
            )
        }
    }
}

@Composable
fun FormularioPlanilla(
    gasto: MediacionGasto,
    cartas: List<CartaDocumentoItem>,
    onGastoChange: (MediacionGasto) -> Unit,
    onAddCarta: () -> Unit,
    onRemoveCarta: (CartaDocumentoItem) -> Unit,
    onUpdateCarta: (String, String, String, String, Double) -> Unit,
    onSaveMediador: () -> Unit,
    onSaveOficina: () -> Unit,
    onSavePago: () -> Unit,
    padding: PaddingValues
) {
    Column(
        modifier = Modifier
            .fillMaxSize()
            .padding(padding)
            .imePadding()
            .verticalScroll(rememberScrollState())
            .padding(16.dp),
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        SectionTitle("Datos de la Mediación")
        OutlinedTextField(value = gasto.nroMediacion, onValueChange = { onGastoChange(gasto.copy(nroMediacion = it)) }, label = { Text("Número de Mediación") }, modifier = Modifier.fillMaxWidth())
        OutlinedTextField(value = gasto.objeto, onValueChange = { onGastoChange(gasto.copy(objeto = it)) }, label = { Text("Objeto") }, modifier = Modifier.fillMaxWidth())
        
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            OutlinedTextField(value = gasto.requerente, onValueChange = { onGastoChange(gasto.copy(requerente = it)) }, label = { Text("Requerente") }, modifier = Modifier.weight(1f))
            OutlinedTextField(value = gasto.requerido, onValueChange = { onGastoChange(gasto.copy(requerido = it)) }, label = { Text("Requerido") }, modifier = Modifier.weight(1f))
        }
        OutlinedTextField(value = gasto.responsablePago, onValueChange = { onGastoChange(gasto.copy(responsablePago = it)) }, label = { Text("Responsable de Pago") }, modifier = Modifier.fillMaxWidth())

        SectionTitle("Patrocinante")
        OutlinedTextField(value = gasto.patrocinanteNombre, onValueChange = { onGastoChange(gasto.copy(patrocinanteNombre = it)) }, label = { Text("Nombre Patrocinante") }, modifier = Modifier.fillMaxWidth())
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            OutlinedTextField(value = gasto.patrocinanteTelefono, onValueChange = { onGastoChange(gasto.copy(patrocinanteTelefono = it)) }, label = { Text("Teléfono") }, modifier = Modifier.weight(1f))
            OutlinedTextField(value = gasto.patrocinanteEmail, onValueChange = { onGastoChange(gasto.copy(patrocinanteEmail = it)) }, label = { Text("Email") }, modifier = Modifier.weight(1f))
        }

        SectionTitle("Mediador")
        OutlinedTextField(value = gasto.mediadorNombre, onValueChange = { onGastoChange(gasto.copy(mediadorNombre = it)) }, label = { Text("Nombre Mediador") }, modifier = Modifier.fillMaxWidth())
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            OutlinedTextField(value = gasto.mediadorMatricula, onValueChange = { onGastoChange(gasto.copy(mediadorMatricula = it)) }, label = { Text("Matrícula") }, modifier = Modifier.weight(1f))
            OutlinedTextField(value = gasto.mediadorRegistro, onValueChange = { onGastoChange(gasto.copy(mediadorRegistro = it)) }, label = { Text("Registro") }, modifier = Modifier.weight(1f))
        }
        OutlinedTextField(value = gasto.mediadorJurisdiccion, onValueChange = { onGastoChange(gasto.copy(mediadorJurisdiccion = it)) }, label = { Text("Jurisdicción") }, modifier = Modifier.fillMaxWidth())
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            OutlinedTextField(value = gasto.mediadorEmail, onValueChange = { onGastoChange(gasto.copy(mediadorEmail = it)) }, label = { Text("Email Mediador") }, modifier = Modifier.weight(1f))
            OutlinedTextField(value = gasto.mediadorWhatsapp, onValueChange = { onGastoChange(gasto.copy(mediadorWhatsapp = it)) }, label = { Text("WhatsApp Mediador") }, modifier = Modifier.weight(1f), keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Phone))
        }
        TextButton(onClick = onSaveMediador, modifier = Modifier.align(Alignment.End)) {
            Icon(Icons.Default.Save, null)
            Spacer(Modifier.width(4.dp))
            Text("Guardar datos mediador")
        }

        SectionTitle("Domicilio Oficina")
        OutlinedTextField(value = gasto.oficinaCalle, onValueChange = { onGastoChange(gasto.copy(oficinaCalle = it)) }, label = { Text("Calle y Número") }, modifier = Modifier.fillMaxWidth())
        OutlinedTextField(value = gasto.oficinaCiudad, onValueChange = { onGastoChange(gasto.copy(oficinaCiudad = it)) }, label = { Text("Ciudad") }, modifier = Modifier.fillMaxWidth())
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            OutlinedTextField(value = gasto.oficinaTelefono, onValueChange = { onGastoChange(gasto.copy(oficinaTelefono = it)) }, label = { Text("Teléfono") }, modifier = Modifier.weight(1f))
            OutlinedTextField(value = gasto.oficinaEmail, onValueChange = { onGastoChange(gasto.copy(oficinaEmail = it)) }, label = { Text("Email de Contacto") }, modifier = Modifier.weight(1f))
        }
        TextButton(onClick = onSaveOficina, modifier = Modifier.align(Alignment.End)) {
            Icon(Icons.Default.Save, null)
            Spacer(Modifier.width(4.dp))
            Text("Guardar datos oficina")
        }

        SectionTitle("Datos de Pago")
        OutlinedTextField(value = gasto.pagoAlias, onValueChange = { onGastoChange(gasto.copy(pagoAlias = it)) }, label = { Text("Alias") }, modifier = Modifier.fillMaxWidth())
        OutlinedTextField(value = gasto.pagoCbu, onValueChange = { onGastoChange(gasto.copy(pagoCbu = it)) }, label = { Text("CBU") }, modifier = Modifier.fillMaxWidth())
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            OutlinedTextField(value = gasto.pagoBanco, onValueChange = { onGastoChange(gasto.copy(pagoBanco = it)) }, label = { Text("Banco") }, modifier = Modifier.weight(1f))
            OutlinedTextField(value = gasto.pagoCuitCuil, onValueChange = { onGastoChange(gasto.copy(pagoCuitCuil = it)) }, label = { Text("CUIT/CUIL") }, modifier = Modifier.weight(1f), keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number))
        }
        OutlinedTextField(value = gasto.pagoCajaAhorro, onValueChange = { onGastoChange(gasto.copy(pagoCajaAhorro = it)) }, label = { Text("Caja de Ahorro Nro.") }, modifier = Modifier.fillMaxWidth())
        TextButton(onClick = onSavePago, modifier = Modifier.align(Alignment.End)) {
            Icon(Icons.Default.Save, null)
            Spacer(Modifier.width(4.dp))
            Text("Guardar datos de pago")
        }

        SectionTitle("Cartas Documento")
        cartas.forEach { item ->
            CartaDocumentoRow(item, onRemove = { onRemoveCarta(item) }, onUpdate = onUpdateCarta)
        }
        Button(onClick = onAddCarta, modifier = Modifier.align(Alignment.End)) {
            Icon(Icons.Default.Add, null)
            Spacer(Modifier.width(4.dp))
            Text("Agregar Carta")
        }
        
        Spacer(Modifier.height(32.dp))
    }
}

@Composable
fun CartaDocumentoRow(item: CartaDocumentoItem, onRemove: () -> Unit, onUpdate: (String, String, String, String, Double) -> Unit) {
    Card(Modifier.fillMaxWidth(), colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.5f))) {
        Column(Modifier.padding(8.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text("Carta Documento", fontWeight = FontWeight.Bold, modifier = Modifier.weight(1f))
                IconButton(onClick = onRemove) { Icon(Icons.Default.Delete, null, tint = MaterialTheme.colorScheme.error) }
            }
            OutlinedTextField(value = item.destinatarioNombre, onValueChange = { onUpdate(item.id, it, item.destinatarioCalle, item.destinatarioCiudad, item.monto) }, label = { Text("Destinatario") }, modifier = Modifier.fillMaxWidth())
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                OutlinedTextField(value = item.monto.toString(), onValueChange = { onUpdate(item.id, item.destinatarioNombre, item.destinatarioCalle, item.destinatarioCiudad, it.toDoubleOrNull() ?: 0.0) }, label = { Text("Monto ($)") }, modifier = Modifier.weight(1f), keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number))
                OutlinedTextField(value = item.destinatarioCiudad, onValueChange = { onUpdate(item.id, item.destinatarioNombre, item.destinatarioCalle, it, item.monto) }, label = { Text("Ciudad") }, modifier = Modifier.weight(1f))
            }
        }
    }
}

@Composable
fun PreviewPlanilla(navController: NavController, gasto: MediacionGasto, padding: PaddingValues) {
    val exporter = getDocumentExporter()
    
    Column(Modifier.fillMaxSize().padding(padding)) {
        Column(Modifier.weight(1f).verticalScroll(rememberScrollState()).padding(24.dp).background(Color.White, RoundedCornerShape(4.dp)).padding(16.dp)) {
            Text("PLANILLA DE GASTOS DE CIERRE DE MEDIACIÓN", fontWeight = FontWeight.ExtraBold, fontSize = 18.sp, textAlign = androidx.compose.ui.text.style.TextAlign.Center, modifier = Modifier.fillMaxWidth())
            Spacer(Modifier.height(16.dp))
            
            PreviewRow("Nro. Mediación:", gasto.nroMediacion)
            PreviewRow("Fecha:", gasto.fechaFormulario)
            PreviewRow("Objeto:", gasto.objeto)
            PreviewRow("Requerente:", gasto.requerente)
            PreviewRow("Requerido:", gasto.requerido)
            PreviewRow("Responsable Pago:", gasto.responsablePago)
            
            HorizontalDivider(Modifier.padding(vertical = 12.dp))
            
            Text("DETALLE ECONÓMICO", fontWeight = FontWeight.Bold, color = MaterialTheme.colorScheme.primary)
            Spacer(Modifier.height(8.dp))
            PreviewMoneyRow("Honorarios Mediador:", gasto.honorariosPesos)
            PreviewMoneyRow("Gastos Administrativos:", gasto.gastosAdministrativosPesos)
            PreviewMoneyRow("Aportes Caja:", gasto.aportesPesos)
            
            if (gasto.cartasDocumento.isNotEmpty()) {
                Text("Cartas Documento:", fontWeight = FontWeight.SemiBold, modifier = Modifier.padding(top = 4.dp))
                gasto.cartasDocumento.forEach {
                    Row(Modifier.fillMaxWidth().padding(start = 16.dp), Arrangement.SpaceBetween) {
                        Text(it.destinatarioNombre, fontSize = 14.sp)
                        Text("$ ${it.monto}", fontSize = 14.sp)
                    }
                }
            }
            
            HorizontalDivider(Modifier.padding(vertical = 12.dp))
            Row(Modifier.fillMaxWidth(), Arrangement.SpaceBetween) {
                Text("TOTAL A ABONAR:", fontWeight = FontWeight.Bold, fontSize = 18.sp)
                Text("$ ${gasto.totalPesos}", fontWeight = FontWeight.Bold, fontSize = 18.sp, color = MaterialTheme.colorScheme.primary)
            }
            
            Spacer(Modifier.height(24.dp))
            Text("DATOS DE PAGO", fontWeight = FontWeight.Bold)
            PreviewRow("Mediador:", gasto.mediadorNombre)
            if (gasto.mediadorMatricula.isNotBlank()) PreviewRow("Matrícula:", gasto.mediadorMatricula)
            PreviewRow("CUIT/CUIL:", gasto.pagoCuitCuil)
            PreviewRow("Alias:", gasto.pagoAlias)
            PreviewRow("CBU:", gasto.pagoCbu)
            PreviewRow("Banco:", gasto.pagoBanco)
            PreviewRow("Caja Ahorro:", gasto.pagoCajaAhorro)
            
            Spacer(Modifier.height(32.dp))
            Text("Documento meramente informativo no constituyendo factura ni recibo.", fontSize = 10.sp, color = Color.Gray, textAlign = androidx.compose.ui.text.style.TextAlign.Center, modifier = Modifier.fillMaxWidth())
        }
        
        BottomAppBar(containerColor = MaterialTheme.colorScheme.surfaceVariant) {
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceEvenly) {
                TrialActionLock(navController = navController, onAction = { exporter.compartirPlanilla(gasto) }) { onClick ->
                    IconButton(onClick = onClick) { Icon(Icons.Default.Share, "Compartir") }
                }
                TrialActionLock(navController = navController, onAction = { exporter.imprimirPlanilla(gasto) }) { onClick ->
                    IconButton(onClick = onClick) { Icon(Icons.Default.Print, "Imprimir") }
                }
                TrialActionLock(navController = navController, onAction = { exporter.exportarPDF(gasto) }) { onClick ->
                    IconButton(onClick = onClick) { Icon(Icons.Default.PictureAsPdf, "PDF") }
                }
                TrialActionLock(navController = navController, onAction = { exporter.enviarWhatsApp(gasto) }) { onClick ->
                    IconButton(onClick = onClick) { Icon(Icons.AutoMirrored.Filled.Chat, "WhatsApp") }
                }
            }
        }
    }
}

@Composable
fun PreviewRow(label: String, value: String) {
    Row(Modifier.fillMaxWidth().padding(vertical = 2.dp)) {
        Text(label, fontWeight = FontWeight.Bold, modifier = Modifier.width(120.dp), fontSize = 14.sp)
        Text(value, fontSize = 14.sp)
    }
}

@Composable
fun PreviewMoneyRow(label: String, value: Double) {
    Row(Modifier.fillMaxWidth().padding(vertical = 2.dp), Arrangement.SpaceBetween) {
        Text(label, fontSize = 14.sp)
        Text("$ $value", fontSize = 14.sp)
    }
}

@Composable
fun SectionTitle(title: String) {
    Text(title, fontWeight = FontWeight.Bold, color = MaterialTheme.colorScheme.primary, modifier = Modifier.padding(top = 8.dp))
}
